import React, { useState } from 'react';
import axios from 'axios';
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50', label: 'Order Received' },
  confirmed: { icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Confirmed' },
  processing: { icon: Package, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Processing' },
  shipped: { icon: Truck, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Delivered' },
  cancelled: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Cancelled' },
};

const statusOrder = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

export const OrderTrackingPage = () => {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOrder(null);

    try {
      // Search by order ID or email
      const response = await axios.get(`${API}/orders`);
      const orders = response.data;
      
      // Find order matching either ID or email
      const foundOrder = orders.find(o => 
        (orderId && o.id === orderId) || 
        (email && o.customer_email.toLowerCase() === email.toLowerCase())
      );

      if (foundOrder) {
        setOrder(foundOrder);
      } else {
        setError('No order found. Please check your order ID or email address.');
      }
    } catch (err) {
      setError('Unable to search orders. Please try again later.');
      console.error('Error searching order:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStatusIndex = (status) => {
    return statusOrder.indexOf(status);
  };

  return (
    <div data-testid="order-tracking-page" className="min-h-screen pt-28 pb-16 bg-cream">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
            Track Your Order
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-4">
            Order Tracking
          </h1>
          <p className="text-warm-brown text-lg max-w-xl mx-auto">
            Enter your order ID or email address to track your order status.
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="bg-white p-8 rounded-2xl shadow-sm mb-8">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-forest mb-2">
                Order ID
              </label>
              <Input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Enter your order ID"
                className="rounded-xl"
                data-testid="order-id-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-forest mb-2">
                Or Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="rounded-xl"
                data-testid="email-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || (!orderId && !email)}
            className="w-full bg-forest hover:bg-forest-light text-white py-6 rounded-full"
            data-testid="track-order-btn"
          >
            {loading ? (
              'Searching...'
            ) : (
              <>
                <Search className="h-5 w-5 mr-2" />
                Track Order
              </>
            )}
          </Button>
        </form>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-8 text-center">
            {error}
          </div>
        )}

        {/* Order Details */}
        {order && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="order-details">
            {/* Order Header */}
            <div className="bg-forest p-6 text-white">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-cream/70 text-sm">Order ID</p>
                  <p className="font-mono text-lg">{order.id.slice(0, 8)}...</p>
                </div>
                <div className="text-right">
                  <p className="text-cream/70 text-sm">Order Date</p>
                  <p className="font-medium">{new Date(order.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="p-6 border-b">
              <h3 className="font-semibold text-forest mb-6">Order Status</h3>
              
              {order.status === 'cancelled' ? (
                <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="font-semibold text-red-700">Order Cancelled</p>
                    <p className="text-sm text-red-600">This order has been cancelled.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  {statusOrder.map((status, index) => {
                    const config = statusConfig[status];
                    const isActive = getCurrentStatusIndex(order.status) >= index;
                    const isCurrent = order.status === status;
                    
                    return (
                      <div key={status} className="flex flex-col items-center flex-1">
                        <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center ${
                          isActive ? config.bg : 'bg-gray-100'
                        }`}>
                          <config.icon className={`h-6 w-6 ${isActive ? config.color : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-xs mt-2 text-center ${
                          isCurrent ? 'font-semibold text-forest' : 'text-gray-500'
                        }`}>
                          {config.label}
                        </p>
                        {index < statusOrder.length - 1 && (
                          <div className={`absolute h-1 w-full top-6 left-1/2 -z-10 ${
                            getCurrentStatusIndex(order.status) > index ? 'bg-green-300' : 'bg-gray-200'
                          }`} style={{ width: 'calc(100% - 3rem)' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Order Info */}
            <div className="p-6 grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-forest mb-3">Delivery Details</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><span className="font-medium">Name:</span> {order.customer_name}</p>
                  <p><span className="font-medium">Email:</span> {order.customer_email}</p>
                  <p><span className="font-medium">Phone:</span> {order.customer_phone}</p>
                  <p><span className="font-medium">Address:</span> {order.delivery_address}</p>
                  <p><span className="font-medium">Delivery Date:</span> {order.delivery_date}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-forest mb-3">Payment</h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="font-medium">Status:</span>{' '}
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      order.payment_status === 'paid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {order.payment_status}
                    </span>
                  </p>
                  <p className="text-2xl font-bold text-forest mt-4">
                    ${order.total_amount?.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="p-6 bg-sand/50">
              <h4 className="font-semibold text-forest mb-4">Order Items</h4>
              <div className="space-y-3">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-4 rounded-xl">
                    <div>
                      <p className="font-medium text-forest">{item.product_name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-mono font-medium">${item.subtotal?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div className="p-6 border-t">
                <h4 className="font-semibold text-forest mb-2">Order Notes</h4>
                <p className="text-sm text-gray-600">{order.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-4">Need help with your order?</p>
          <a 
            href="/contact" 
            className="text-gold hover:text-terracotta font-medium transition-colors"
          >
            Contact Us →
          </a>
        </div>
      </div>
    </div>
  );
};
